import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import type { AffectedUser, IssueCluster, Severity, SignalKind } from "@backstory/protocol";
import { DEFAULT_SEVERITY } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { useAuth } from "../../auth/store";
import { EndUser } from "../../components/EndUser";
import { Hint } from "../../components/ui";
import { RANGE_PRESETS, customDateBounds, rangeFromSearch, rangeToSearch, resolveRange, type RangeState } from "./range";
import type { TimeRange } from "./api";

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const KIND_LABELS: Record<SignalKind, string> = {
  rage_click: "Rage click",
  dead_click: "Dead click",
  error_click: "Error click",
  thrashed_cursor: "Thrashed cursor",
  form_abandonment: "Form abandonment",
  field_struggle: "Field struggle",
  failed_request: "Failed request",
  slow_request: "Slow request",
  js_error: "JS error",
  frozen_ui: "Frozen UI",
  layout_instability: "Layout instability",
  slow_page: "Slow page",
  navigation_loop: "Navigation loop",
  back_button_bounce: "Back-button bounce",
  blank_page: "Blank page",
  broken_resource: "Broken resource",
  console_error_burst: "Console error burst",
  offline_period: "Offline period",
  crash: "Crash",
  accessibility_trap: "Accessibility trap",
  consent_friction: "Consent friction",
  webrtc_connect_failure: "Call failed to connect",
  webrtc_media_permission_denied: "Camera or mic permission denied",
  webrtc_device_unavailable: "Camera or mic unavailable",
  webrtc_one_way_media: "One-way audio or video",
  webrtc_freeze: "Video freeze",
  webrtc_audio_dropout: "Audio dropout",
  webrtc_high_latency: "High call latency",
  webrtc_packet_loss: "Call packet loss",
  webrtc_relay_fallback: "Relayed through TURN",
  webrtc_cpu_limited: "Encoder limited by CPU",
  webrtc_reconnect_storm: "Call reconnect storm",
  webrtc_low_resolution: "Low call resolution",
  webrtc_call_abandoned: "Call abandoned early",
  monitor_trigger: "Monitor trigger",
  backend_exception: "Backend exception",
  backend_error_log: "Backend error log",
  metric_threshold: "Metric alert",
  custom: "Custom",
};
export const ALL_KINDS = Object.keys(KIND_LABELS) as SignalKind[];
export const SEVERITIES: Severity[] = ["critical", "high", "medium", "low", "info"];
export const STATUSES: IssueCluster["status"][] = ["open", "regressed", "acknowledged", "resolved", "muted"];

export function kindLabel(k: SignalKind): string {
  return KIND_LABELS[k] ?? k;
}

const SEV_CLASS: Record<Severity, string> = { critical: "danger", high: "danger", medium: "warn", low: "info", info: "neutral" };
const SEV_ICON: Record<Severity, string> = { critical: "‼", high: "!", medium: "▲", low: "•", info: "i" };

/** Severity badge: icon + label, never color alone. */
export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span className={`badge ${SEV_CLASS[severity]}`} title={`Severity: ${severity}`}>
      <span aria-hidden="true">{SEV_ICON[severity]}</span> {statusLabel(severity)}
    </span>
  );
}

const STATUS_CLASS: Record<IssueCluster["status"], string> = { open: "info", regressed: "danger", acknowledged: "neutral", resolved: "ok", muted: "neutral" };
/** A snooze that has not run out yet. */
export function isSnoozed(until: string | undefined, now = Date.now()): boolean {
  return !!until && new Date(until).getTime() > now;
}
/**
 * What the title left out, so two issues that were named the same way still
 * read as different rows. A title that already names the element and the page
 * adds nothing here.
 */
export function issueIdentity(c: Pick<IssueCluster, "title" | "selector" | "route" | "errorMessage">): string | undefined {
  const title = c.title.toLowerCase();
  const parts: string[] = [];
  const selector = c.selector?.trim();
  const message = c.errorMessage?.trim();
  if (selector && !title.includes(selector.toLowerCase())) parts.push(selector);
  else if (!selector && message && !title.includes(message.toLowerCase())) {
    parts.push(message.length > 80 ? `${message.slice(0, 77)}…` : message);
  }
  const route = c.route?.trim();
  if (route && route !== "/" && !title.includes(route.toLowerCase())) parts.push(route);
  return parts.length ? parts.join(" · ") : undefined;
}

export function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}
export function StatusBadge({ status }: { status: IssueCluster["status"] }) {
  return <span className={`badge ${STATUS_CLASS[status]}`}>{statusLabel(status)}</span>;
}

/** A cluster is "regressing" when the last 24h exceeds 3× its daily average over 7 days (or the server says so). */
export function isRegressing(c: Pick<IssueCluster, "status" | "count24h" | "count7d">): boolean {
  if (c.status === "regressed") return true;
  const dailyAvg = c.count7d / 7;
  return c.count7d >= 7 && c.count24h > dailyAvg * 3;
}

export function RegressionBadge({ cluster }: { cluster: Pick<IssueCluster, "status" | "count24h" | "count7d"> }) {
  if (!isRegressing(cluster)) return null;
  return (
    <span className="badge danger" title="The last 24 hours are more than 3× the daily average for the week">
      <span aria-hidden="true">↑</span> Rising
    </span>
  );
}

export function defaultSeverity(k: SignalKind): Severity {
  return DEFAULT_SEVERITY[k] ?? "medium";
}

/**
 * Impact told as people first. A count answers "how bad", but the question that
 * follows is always "who", and on lists of sessions and calls that answer is
 * already a name — so an issue or error row leads with the person hit most
 * recently and keeps the totals underneath. Traffic nobody identified has no
 * name to give, and there the count stands alone rather than pretending.
 */
export function AffectedUsers({ users, count, sessions }: { users?: AffectedUser[]; count: number; sessions: number }) {
  const lead = users?.[0];
  const rest = Math.max(count - 1, 0);
  return (
    <span className="cell-stack">
      {lead ? <EndUser id={lead.id} name={lead.name} />
        : count > 0 ? <strong className="mono">{count.toLocaleString()} {count === 1 ? "user" : "users"}</strong>
        : <strong className="muted" title="None of these sessions identified a signed-in user">Anonymous</strong>}
      <small title={users && users.length > 1 ? users.map((u) => u.name || u.id).join(", ") : undefined}>
        {lead && rest > 0 ? `+${rest.toLocaleString()} more · ` : ""}
        {sessions.toLocaleString()} {sessions === 1 ? "session" : "sessions"}
      </small>
    </span>
  );
}

/**
 * Who an issue is assigned to: one of the organization's members, chosen from
 * a list rather than typed. The stored value is the member's email. An
 * assignee who has since left the organization still shows, so the field
 * never silently reads as empty.
 */
export function AssigneeSelect({ value, onChange, label = "Assignee", unassigned = "Unassigned", disabled, className, style }: {
  value: string;
  onChange: (email: string) => void;
  label?: string;
  unassigned?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const auth = useAuth();
  const orgId = auth.currentOrgId ?? "";
  const members = useQuery({ queryKey: queryKeys.members(orgId), queryFn: () => api.orgs.members(orgId), enabled: !!orgId, staleTime: 60_000 });
  const list = members.data ?? [];
  const known = list.some((m) => m.email === value);
  return (
    <select aria-label={label} className={className} style={style} value={value} disabled={disabled || members.isPending} onChange={(e) => onChange(e.target.value)} autoComplete="off">
      <option value="">{members.isPending ? "Loading members…" : unassigned}</option>
      {list.map((m) => (
        <option key={m.userId} value={m.email}>{m.name ? `${m.name} (${m.email})` : m.email}</option>
      ))}
      {value && !known && <option value={value}>{value} (no longer a member)</option>}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Range picker (one row, above the charts; presets first, custom last)
// ---------------------------------------------------------------------------

export function useRange(): { state: RangeState; range: TimeRange; set: (r: RangeState) => void } {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const state = useMemo(() => rangeFromSearch(search), [search]);
  const range = useMemo(() => resolveRange(state), [state]);
  const set = (r: RangeState) => {
    const next: Record<string, unknown> = { ...search };
    delete next.range;
    delete next.from;
    delete next.to;
    void navigate({ to: pathname as never, search: { ...next, ...rangeToSearch(r) } as never, replace: true });
  };
  return { state, range, set };
}

export function RangePicker() {
  const { state, set } = useRange();
  const [custom, setCustom] = useState(state.preset === "custom");
  const bounds = customDateBounds();
  const fromMax = state.to && state.to < bounds.max ? state.to : bounds.max;
  const toMin = state.from && state.from > bounds.min ? state.from : bounds.min;
  // A date field reports an empty value until every part is filled. Writing that
  // back into the URL, and into the field, wipes the digits still being typed.
  const commit = (from: string | undefined, to: string | undefined) => {
    if (from && to && from > to) to = from;
    set({ preset: "custom", from, to });
  };
  return (
    <div className="ins-range" role="group" aria-label="Date range">
      {RANGE_PRESETS.map((p) => (
        <button key={p.id} type="button" className="btn" aria-pressed={state.preset === p.id} onClick={() => { setCustom(false); set({ preset: p.id }); }}>
          {p.id === "24h" ? "24h" : p.id === "7d" ? "7d" : "30d"}
        </button>
      ))}
      <button type="button" className="btn" aria-pressed={state.preset === "custom"} onClick={() => setCustom(true)}>
        Custom
      </button>
      {(custom || state.preset === "custom") && (
        <>
          <DateField label="From date" committed={state.from ?? ""} min={bounds.min} max={fromMax} onCommit={(from) => commit(from, state.to)} />
          <span className="muted small">→</span>
          <DateField label="To date" committed={state.to ?? ""} min={toMin} max={bounds.max} onCommit={(to) => commit(state.from, to)} />
        </>
      )}
    </div>
  );
}

/** A date field that keeps partial typing and only publishes a finished day. */
export function DateField({ id, label, committed, min, max, onCommit, className }: { id?: string; label: string; committed: string; min: string; max: string; onCommit: (value: string) => void; className?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    if (el.value !== committed) el.value = committed;
  }, [committed]);
  return (
    <input
      ref={ref}
      id={id}
      type="date"
      className={className}
      aria-label={label}
      min={focused ? undefined : min}
      max={focused ? undefined : max}
      defaultValue={committed}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const value = e.target.value;
        if (/^\d{4}-\d{2}-\d{2}$/.test(value) && value >= min && value <= max) onCommit(value);
      }}
      onBlur={(e) => {
        setFocused(false);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(e.target.value) || e.target.value < min || e.target.value > max) e.target.value = committed;
      }}
      autoComplete="off"
    />
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

export function Card({ title, children, actions, className }: { title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <section className={`card ins-card ${className ?? ""}`}>
      {(title || actions) && (
        <div className="ins-card-head">
          {title && <h2>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * The small "i" button that explains a metric. Press to pin the explanation,
 * hover to preview it. Inside `.metric-wrap` it sits in the card's top-right
 * corner; elsewhere it flows inline after a heading.
 */
export function InfoButton({ about, hint }: { about: string; hint: string }) {
  return (
    <Hint text={hint} toggle>
      <button type="button" className="tile-info" aria-label={`About ${about}`}>
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.25" />
          <path d="M8 7.25v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="8" cy="4.7" r="0.8" fill="currentColor" />
        </svg>
      </button>
    </Hint>
  );
}

/** A stat tile with an info button in its corner. */
export function MetricWrap({ about, hint, children }: { about: string; hint: string; children: ReactNode }) {
  return (
    <div className="metric-wrap">
      {children}
      <InfoButton about={about} hint={hint} />
    </div>
  );
}

export function Delta({ value, digits = 1, suffix = "", upIsGood = true }: { value: number; digits?: number; suffix?: string; upIsGood?: boolean }) {
  if (!Number.isFinite(value)) return <span className="muted">—</span>;
  const good = upIsGood ? value >= 0 : value <= 0;
  const cls = value === 0 ? "" : good ? "good" : "bad";
  return (
    <span className={`ins-delta ${cls}`}>
      <span aria-hidden="true">{value > 0 ? "▲" : value < 0 ? "▼" : "•"}</span>
      {value > 0 ? "+" : value < 0 ? "−" : ""}
      {Math.abs(value).toFixed(digits)}
      {suffix}
    </span>
  );
}

/** Deep link to the replay at a moment. */
export type EvidenceHref = { to: string; params: Record<string, string>; search: Record<string, unknown>; label: string; title: string };

/**
 * Where a piece of evidence opens. Backend Signals cite "trace:<id>" (a
 * backend request) or "metric:<ruleId>" (the alerting metric); anything else
 * is a session replay.
 */
export function evidenceHref(id: string, tMs?: number): EvidenceHref {
  if (id.startsWith("trace:")) {
    const traceId = id.slice(6);
    return { to: "/backend/traces/$traceId", params: { traceId }, search: {}, label: `request ${traceId.slice(0, 8)}…`, title: "Open the backend request" };
  }
  if (id.startsWith("metric:")) {
    return { to: "/backend/metrics", params: {}, search: {}, label: "metric chart", title: "Open backend metrics" };
  }
  return { ...replayHref(id, tMs), label: `session ${id.slice(0, 8)}…`, title: "Watch the session" };
}

export function replayHref(sessionId: string, tMs?: number): { to: "/sessions/$id"; params: { id: string }; search: { t?: number } } {
  return { to: "/sessions/$id", params: { id: sessionId }, search: tMs !== undefined ? { t: Math.round(tMs) } : {} };
}
